import React, { Suspense, useEffect, useMemo } from 'react';
import { CustomTable } from '../components/CustomTable';
import { warehousesTableHeaders } from '../utils/Enums/TableHeaders';
import { PaginationComponent } from '../components/Pagination';
import {
    ReturnedItemsGroup,
    TableComponentProps,
    Warehouse,
    WarehousesTableInnerProps,
} from '../models/table-models';
import { useTableData } from '../utils/data-hooks';
import { usePagination } from '../utils/pagination-hooks';
import { resourceCache } from 'use-async-resource';
import { Spinner } from 'react-bootstrap';
import {
    useDeleteWarehouse,
    useGetReturnedItems,
    useGetWarehouses,
} from '../utils/warehouses-hooks';
import { AddActionComponent } from '../components/AddActionComponent';
import { AddEditWarehouse } from './AddEditWarehouse';
import { useTranslation } from 'react-i18next';
import { NoDataComponent } from '../components/NoDataComponent';
import { useNavigate } from 'react-router-dom';
import {CustomToastContent} from "../components/CustomToastContent";
import {toast} from "react-toastify";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";
import { ReturnedItemsInner } from './warehouses/ReturnedItemsTable';
import { Role } from '../utils/Enums/Roles';

export const Warehouses = ({ filter, pagination }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    const [getWarehouses, fetchNewWarehouses] = useTableData<Warehouse[]>(useGetWarehouses, pagination, filter);
    const [getReturnedItems, fetchNewReturnedItems] = useTableData<ReturnedItemsGroup[]>(useGetReturnedItems, pagination, filter);

    const refetchWarehouseHook = async () => {
        resourceCache(useGetWarehouses).clear();
        await fetchNewWarehouses({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            pagination,
            filter,
        });
    };

    const refetchReturnedItemsHook = async () => {
        resourceCache(useGetReturnedItems).clear();
        await fetchNewReturnedItems({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            pagination,
            filter,
        });
    };

    return (
        <>
            <div className='d-flex w-100 justify-content-between mb-3'>
                <AddActionComponent
                    accessActionRoles={[Role.WAREHOUSE]}
                    addComponent={AddEditWarehouse}
                    title={t('addWarehouse')}
                    refetchEntitiesHook={refetchWarehouseHook}
                />
            </div>
            <Suspense fallback={<Spinner animation='border' />}>
                <WarehousesInner
                    {...{ pagination, filter }}
                    getEntities={getWarehouses}
                    refetchEntitiesHook={refetchWarehouseHook}
                />
            </Suspense>
            <Suspense fallback={<Spinner animation='border' />}>
                <ReturnedItemsInner
                    {...{ pagination, filter }}
                    getEntities={getReturnedItems}
                    refetchEntitiesHook={refetchReturnedItemsHook}
                />
            </Suspense>
        </>
    );
};

const WarehousesInner = ({ filter, pagination, refetchEntitiesHook }: WarehousesTableInnerProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const p = usePagination(pagination.pageSize);
    const [getWarehouses, fetchNewWarehouses] = useTableData<Warehouse[]>(
        useGetWarehouses,
        { ...p, pageSize: pagination.pageSize },
        filter
    );
    const warehouseRequestParams = useMemo(
        () => ({
            'pager.page': p.currentPage,
            'pager.itemsPerPage': p.pageSize,
            pagination: {
                pageSize: p.pageSize,
                pageStart: p.pageStart,
                setPagination: p.setPagination,
                currentPage: p.currentPage,
                setCurrentPage: p.setCurrentPage,
            },
            filter,
        }),
        [filter, p.currentPage, p.pageSize, p.pageStart, p.setCurrentPage, p.setPagination]
    );

    useEffect(() => {
        resourceCache(useGetWarehouses).clear();
        fetchNewWarehouses(warehouseRequestParams);
    }, [fetchNewWarehouses, warehouseRequestParams]);

    const useDelete = async (warehouse: Warehouse) => {
        await useDeleteWarehouse(warehouse);
        refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('warehouseDeleted')]}/>, toastOptionsTopRight);
    }

    const { items, pager } = getWarehouses();
    const sortedItems = useMemo(() => {
        const getWarehouseSortOrder = (warehouse: Warehouse) => {
            if (!warehouse.type) return 0;
            if (warehouse.type === 2) return 2;
            if (warehouse.type === 1) return 3;
            return 4;
        };

        return [...items].sort((a, b) => getWarehouseSortOrder(a) - getWarehouseSortOrder(b));
    }, [items]);
    const userInventoriesRowIndex = sortedItems.filter((warehouse) => !warehouse.type).length;
    const navigate = useNavigate();

    return (
        <>
            {items && items.length > 0 ? (
                <>
                    <CustomTable
                        columns={{
                            headings: warehousesTableHeaders.filter((header) => header !== 'type'),
                            items: sortedItems.map(({ warehouseName, mainGroup, subGroups }) => [
                                warehouseName,
                                mainGroup,
                                subGroups,
                            ]),
                        }}
                        accessEditRoles={[Role.WAREHOUSE]}
                        accessActionRoles={[Role.WAREHOUSE]}
                        editComponent={AddEditWarehouse}
                        viewComponent={(id) => {
                            navigate(`/warehouses/${id}`);
                        }}
                        actionHook={useDelete}
                        tableData={sortedItems}
                        customRows={[
                            {
                                cells: [t('userInventories'), '', ''],
                                onClick: () => navigate('/inventories'),
                                insertIndex: userInventoriesRowIndex,
                            },
                        ]}
                        {...{ refetchEntitiesHook }}
                    />
                    <PaginationComponent
                        recordsCount={pager.totalCount}
                        pagination={{ ...p, pageSize: pagination.pageSize }}
                    />
                </>
            ) : (
                <NoDataComponent noDataText={t('warehouses')} />
            )}
        </>
    );
};
